import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';

import { filter, forkJoin, of, Subscription, switchMap, tap } from 'rxjs';
import cloneDeep from 'lodash/cloneDeep';

import { Account } from '@shared/account/models';
import { DisplayOrdersPage } from '@shared/square/enum';
import { COUNTRIES } from '@shared/core';
import { getDisplayOrdersPage } from '@shared/square/functions';
import { AppIcon, AppIconSize } from '@tablesready/frontend-icons';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { WhoService } from '../../../../../services';
import { SquareService } from '../../services';

@Component({
  selector: 'tablesready-square-settings',
  templateUrl: './square.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SquareComponent implements OnInit, OnDestroy {
  @Input() disabled: boolean = true;
  icon = AppIcon;
  size = AppIconSize;
  squareScopes: string;
  squareAppId: string;
  isSquared: boolean;
  squareOptions: {
    parseOrderTicket: boolean;
    hideNoPhone: boolean;
    showOrderItems: boolean;
    hideReceiptNum: boolean;
    filterCategories: string[] | boolean;
    filterCategoryOptions?: string[];
    sendReadyOnCompleted: boolean;
    squareSyncOrders: boolean;
    squareSyncCustomers?: boolean;
    displayOrdersPage?: DisplayOrdersPage;
  };
  squareLocations = [];
  squareLocationsSubscriptions: Array<string> = [];
  squareCategories = [];
  account: Account;
  displayOrdersPageOptions: {value: DisplayOrdersPage; label: string; disabled?: boolean}[] = [
    {
      value: DisplayOrdersPage.waitlist,
      label: 'Waitlist',
    },
    {
      value: DisplayOrdersPage.togo,
      label: 'To Go list',
    },
  ];

  private subscriptions = new Subscription();

  constructor(
    private readonly squareService: SquareService,
    private readonly alertsService: AlertsService,
    private readonly whoService: WhoService,
    private readonly cdr: ChangeDetectorRef,
    private readonly authRestaurantService: AuthRestaurantService,
  ) {}

  ngOnInit() {
    this.squareScopes = 'MERCHANT_PROFILE_READ%20PAYMENTS_READ%20CUSTOMERS_READ%20ITEMS_READ%20ORDERS_READ%20ORDERS_WRITE%20CUSTOMERS_WRITE';
    this.isSquared = false;

    this.subscriptions.add(
      this.authRestaurantService.select()
        .pipe(
          filter(user => user.location !== undefined),
          switchMap((user) => {
            this.account = user.account;
            let squareDataRequest = of(null);
            let squareCategoriesRequest = of(null);
            if (user?.location?.integrations?.square) {
              squareDataRequest = this.squareService.getData();
              squareCategoriesRequest = this.squareService.listCategories();
            }
            const getAppIdRequest = this.squareService.getAppId();

            return forkJoin([of(user), squareDataRequest, squareCategoriesRequest, getAppIdRequest]);
          }),
          tap(([user, squareData, squareCategoryNames, squareId]) => {
            const location = user.location;
            this.squareOptions = location.integrations
              && location.integrations.square
              && cloneDeep(location.integrations.squareOptions)
              || {
                hideNoPhone: false,
                showOrderItems: false,
                hideReceiptNum: false,
                parseOrderTicket: false,
                filterCategories: this.filterCategoriesIsChecked,
                sendReadyOnCompleted: false,
                squareSyncOrders: false,
                squareSyncCustomers: false,
                displayOrdersPage: DisplayOrdersPage.waitlist,
              };
            this.squareOptions.displayOrdersPage = getDisplayOrdersPage({location, account: user.account});
            this.displayOrdersPageOptions[1].label = `To Go list${!user.location.displayOptions.togo ? ' [Enable this feature]': ''}`
            this.squareOptions.filterCategories = this.filterCategoriesIsChecked;
            this.squareAppId = String(squareId);
            this.squareCategories = squareCategoryNames;

            if (squareData) {
              this.squareLocations = (squareData as any).locations.sort((a, b) => a.name.localeCompare(b.name));
              if ((squareData as any).subscriptions) {
                this.squareLocationsSubscriptions = (squareData as any).subscriptions;
              }
              this.squareLocations = this.updateLocationsWithSubscriptions();
            }

            this.isSquared = !!location.integrations?.square;
            this.cdr.markForCheck();
          }),
        )
        .subscribe()
    );
  }

  // We need this for transforming [string] to boolean
  // Example:['true'] => true
  get filterCategoriesIsChecked(): boolean {
    if (Array.isArray(this.squareOptions?.filterCategories)) {
      return (this.squareOptions?.filterCategories)?.length ? this.squareOptions?.filterCategories[0] === 'true' && Boolean(this.squareOptions?.filterCategoryOptions?.length) : false;
    }

    // New data type for filterCategories is Boolean, not an array
    // TO DO: data should all be converted to boolean
    return this.squareOptions?.filterCategories && Boolean(this.squareOptions?.filterCategoryOptions?.length);
  }

  updateLocationsWithSubscriptions() {
    // merge subscriptions data with locations by location id
    return this.squareLocations.map((location) => {
      location.selected = this.squareLocationsSubscriptions.includes(location.id);
      return location;
    });
  }

  disconnectSquare() {
    this.squareService.disconnectSquare().subscribe(
      () => {
        this.alertsService.add({
          type: IAlertTypes.success,
          heading: 'Success:',
          body: 'disconnected Square account',
        });
      },
      (error) => {
        this.alertsService.add({
          type: IAlertTypes.danger,
          heading: 'Error disconnecting Square account:',
          body: `${error}`,
        });
      }
    );
  }

  subscribeLocations() {
    const subData = this.squareLocations
      .filter(loc => loc.selected)
      .map(sub => sub.id);

    this.updateSquareOptions();
    this.squareService.subscribeLocations(subData).subscribe(
      (response) => {
        this.squareLocationsSubscriptions = response;
        this.squareLocations = this.updateLocationsWithSubscriptions();
        this.alertsService.add({
          type: IAlertTypes.success,
          heading: 'Success:',
          body: 'subscribed to locations',
        });
        this.cdr.detectChanges();
      },
      (error) => {
        this.alertsService.add({
          type: IAlertTypes.danger,
          heading: 'Error subscribing to location webhooks:',
          body: `${error}`,
        });
      }
    );
  }

  updateSquareOptions() {
    this.whoService.updateSquareOptions(this.squareOptions).subscribe(
      () => {
        this.alertsService.add({
          type: IAlertTypes.success,
          heading: 'Success:',
          body: 'updated Square options.',
        });
        this.cdr.detectChanges();
      },
      (error) => {
        this.alertsService.add({
          type: IAlertTypes.danger,
          heading: 'Error:',
          body: `${error}`,
        });
      }
    );
  }

  updateLocations() {
    this.squareService.updateLocations().subscribe(
      (response) => {
        this.squareLocations = response.sort((a, b) => a.name.localeCompare(b.name));
        this.squareLocations = this.updateLocationsWithSubscriptions();
        this.alertsService.add({
          type: IAlertTypes.success,
          heading: 'Success:',
          body: 'updated locations list',
        });
        this.cdr.markForCheck();
      },
      (error) => {
        this.alertsService.add({
          type: IAlertTypes.danger,
          heading: 'Error updating location:',
          body: `${error}`,
        });
      }
    );
  }

  syncOrdersChanged(value: boolean): void {
    if (value && this.squareOptions.squareSyncCustomers) {
      this.squareOptions.squareSyncCustomers = false;
    }
  }

  syncCustomersChanged(value: boolean): void {
    if (value && this.squareOptions.squareSyncOrders) {
      this.squareOptions.squareSyncOrders = false;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  authorize() {
    this.squareService.setState()
      .pipe(tap(({state}) => {
        window.location.href = `https://squareup.com/t/cmtp_performance/pr_cross_product/d_partnerships/p_TablesReady/cn_oauth/?route=oauth2/authorize?client_id=${this.squareAppId}&scope=${this.squareScopes}&state=${state}&session=false`;
      }))
      .subscribe();
  }

  protected readonly countries = COUNTRIES;
}
